import { ref, type Ref } from 'vue';
import { useGetTranslates } from '~/domains/i18n';
import { useCompare, COMPARE } from '~/domains/compare';
import { useBasketStats } from '~/domains/basket';
import { useCategoriesTree } from '~/domains/category';
import {
  getUserInfoViewModel,
  useOnChangeCurrency,
  useUserPreferences,
} from '~/domains/user';
import { useRoute } from '~/infrastructure';
import useInitLanguage from './useInitLanguage.ts';

interface ComposableReturn {
  loading: Ref<boolean>;
  initRequiredRequests: () => Promise<void>;
}

export default function useDefaultLayout(): ComposableReturn {
  const loading = ref(false);
  const route = useRoute();
  const { getCompare } = useCompare();
  const { getBasketStats } = useBasketStats();
  const { fetchCategoriesTree } = useCategoriesTree();
  const { getUserPreferences } = useUserPreferences();
  const { getTranslates } = useGetTranslates();
  const { fetchUserInfo } = getUserInfoViewModel();
  const { initLanguage } = useInitLanguage();

  const initRequiredRequests = async (): Promise<void> => {
    loading.value = true;

    await initLanguage();

    const requests = [
      getTranslates(),
      getBasketStats(),
      fetchCategoriesTree(),
      getUserPreferences(),
    ];

    if (route.path !== COMPARE) {
      requests.push(getCompare());
    }

    try {
      await Promise.all(requests);
    } catch (err) {
      console.log(err);
    } finally {
      loading.value = false;
    }
  };

  useOnChangeCurrency(() => {
    fetchUserInfo();
  });

  return {
    loading,
    initRequiredRequests,
  };
}
